import { Box, Divider, lighten, Stack } from "@mui/material";
import Link from "next/link";
import React from "react";
import { SSButton } from "src/components/base/components/SSButton";
import { SSText } from "src/components/base/components/SSText";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { global_config } from "src/shared/global_config";
import { routes } from "src/shared/routes";
import { TPassGroupCardProps } from "src/shared/types/pass-group-card-props";
import { TTeamConfig } from "src/shared/types/teamConfig.types";
import { openPdfInNewTab } from "src/utils/utils";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import MeetingRoomRoundedIcon from "@mui/icons-material/MeetingRoomRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import QrCode2Icon from "@mui/icons-material/QrCode2";

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => <>Success!</>,
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Your New York Yankees Pass purchase is confirmed. Please login to the
          Ballpark App{" "}
          <Link href={"https://bp.mlb.com/Wallet"} target="_blank">
            <span
              style={{
                color: "#ffffff",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                cursor: "pointer",
              }}
            >
              here
            </span>
          </Link>{" "}
          to access your tickets.
        </>
      );
    },
  };

export function PassGroupCard(props: TPassGroupCardProps) {
  const { sm: smUp } = useCssMediaQuery("up");
  const theme = useSSTheme();
  const [isLoading, setIsLoading] = React.useState(false);

  const onClickPurchase = async () => {
    setIsLoading(true);
    await props?.onClickPurchase?.();
  };

  React.useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

  const descriptionList = [
    {
      id: 1,
      icon: <GroupRoundedIcon />,
      text: "General admission access to Yankee Stadium in a non-assigned, Standing-Room-Only location to all Yankees’ home games originally scheduled to be played during the month that it is valid, including high-demand and weekend games (Opening Day excluded)",
    },
    {
      id: 2,
      icon: <QrCode2Icon />,
      text: "Easy entry: scan your mobile-only Yankees Ballpark Pass via the MLB Ballpark app directly from your iPhone and Android phone",
    },
    {
      id: 3,
      icon: <PhoneIphoneRoundedIcon />,
      text: "Streamlined management via the MLB Ballpark app",
    },
  ];

  return (
    <Stack
      p={smUp ? 4 : 2}
      spacing={2}
      border={`1px solid ${theme.colors.border}`}
      borderRadius={2.5}
      alignItems={"flex-start"}
      overflow={"hidden"}
      height={"100%"}
      sx={props?.slot?.wrapper?.sx}
    >
      <Stack width={"100%"}>
        <SSText
          variant="h2"
          color={theme.colors.teamPrimary}
          lineHeight={1.1}
          fontWeight={500}
          textAlign={"center"}
        >
          With the Yankees Ballpark Pass, you’ll enjoy:
        </SSText>
      </Stack>

      <Box>
        <Stack>
          {descriptionList.map((itm, idx) => {
            return (
              <Box key={itm.id}>
                <Stack
                  py={1.5}
                  direction={"row"}
                  spacing={smUp ? 2 : 1}
                  alignItems={smUp ? "center" : "flex-start"}
                >
                  <Box
                    sx={{
                      minWidth: "42px",
                      minHeight: "42px",
                      width: "42px",
                      height: "42px",
                      borderRadius: "50%",
                      backgroundColor: lighten(theme.colors.teamPrimary, 0.95),
                      color: theme.colors.teamPrimary,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {itm.icon}
                  </Box>
                  <SSText variant="body" color={"text.primary"}>
                    {itm.text}
                  </SSText>
                </Stack>
                {idx < descriptionList?.length - 1 ? <Divider /> : null}
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box flexGrow={1} />

      <Stack spacing={1} sx={{ marginTop: "-4px !important" }}>
        <SSText
          variant="h4"
          color={theme.colors.teamPrimary}
          lineHeight={1.1}
          fontWeight={500}
          textAlign={"center"}
        >
          Ready to catch all the home games you can during a month?
        </SSText>

        <SSText variant="body" color={"text.primary"} textAlign={"center"}>
          Choose the month you can make the most, or better yet – don’t let your
          game access run out. Purchase a pass for each month of the season now.
          LIMITED QUANTITIES AVAILABLE
        </SSText>
      </Stack>

      {!props?.isPurchaseButtonHidden ? (
        <Stack width={"100%"} direction={"row"} justifyContent={"center"}>
          <SSButton
            variant="contained"
            onClick={onClickPurchase}
            loading={isLoading}
            disabled={true}
            x
            size="large"
            sx={{ minWidth: smUp ? "350px" : "100%" }}
          >
            SOLD OUT
            {/* {props?.purchaseButtonText || "Purchase Now"} */}
          </SSButton>
        </Stack>
      ) : null}

      {props?.isLoginTextVisible ? (
        <Stack width={"100%"} justifyContent={"center"} alignItems={"center"}>
          <SSText variant="body" color={theme.colors.gray[300]}>
            Already a member?{" "}
            <span
              onClick={props?.onClickLogin}
              style={{
                textDecoration: "underline",
                textUnderlineOffset: "4px",
                fontWeight: 500,
                cursor: "pointer",
                color: theme.colors.teamPrimary,
              }}
            >
              Sign in
            </span>
          </SSText>
        </Stack>
      ) : null}
    </Stack>
  );
}

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 2,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();

  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Which games are included with the Yankees Ballpark Pass?",
    answer:
      "The Yankees Ballpark Pass includes all Yankees’ home games originally scheduled to be played in the month that it is valid, including high-demand and weekend games (Opening Day excluded).",
  },
  {
    question: "How many Yankees Ballpark Passes can I buy?",
    answer:
      "Fans may buy up to four (4) Yankees Ballpark Passes per account. Sales of the Yankees Ballpark Pass are restricted to residents of New York, New Jersey, Connecticut and Pennsylvania, with residency determined by the billing address of the credit card used for purchase.",
  },
  {
    question: "Can I get printed tickets when using the Yankees Ballpark Pass?",
    answer:
      "No. The Yankees Ballpark Pass is only accessible through the MLB Ballpark app, which must be downloaded onto an iPhone or Android phone. There are no printed tickets.",
  },
  {
    question: "Which mobile phones are supported?",
    answer:
      "The MLB Ballpark app and the Yankees Ballpark Pass are only compatible with iPhones and Android phones. You cannot purchase the Yankees Ballpark Pass if you do not have an iPhone or Android phone.",
  },
  {
    question: "How do I access the Yankees Ballpark Pass?",
    answer:
      "Download the MLB Ballpark app to an iPhone or Android phone and create or login to an MLB account using the same email address used to purchase the Yankees Ballpark Pass to access a digital QR code that will be scanned for admission to the applicable game.",
  },
  {
    question: "Does the Yankees Ballpark Pass include an assigned seat?",
    answer:
      "No. The Yankees Ballpark Pass only guarantees general admission access to Yankee Stadium in a non-assigned Standing-Room-Only location such as the Mastercard Batters’ Deck, FreshDirect Terrace, Toyota Terrace, Budweiser Party Decks, Stella Artois Landing and Michelob ULTRA Clubhouse. The Yankees Ballpark Pass does not provide access to any ticketed seat location, café seats/assigned Standing-Room-Only locations or portable folding chairs. Standing is not permitted in entryways, aisles, areas of seating sections or in wheelchair and companion platform seating areas.",
  },
  {
    question: "Will I ever be assigned a seat?",
    answer:
      "While the Yankees Ballpark Pass only guarantees general admission access to Yankee Stadium in a non-assigned Standing-Room-Only location, for some games, you may have an opportunity to upgrade to a designated non-premium seat, subject to availability, via your My Yankees Account on the day of the game. Fans who wish to sit together must purchase all Yankees Ballpark Passes in one (1) transaction. THERE IS NO GUARANTEE THAT ANY NON-PREMIUM SEATS WILL BE MADE AVAILABLE. NON-PREMIUM SEAT UPGRADES ARE IN THE SOLE AND ABSOLUTE DISCRETION OF THE YANKEES.",
  },
  {
    question: "Is the Yankees Ballpark Pass refundable?",
    answer: "No. Once purchased, the Yankees Ballpark Pass is non-refundable.",
  },
  {
    question:
      "Can I resell my Yankees Ballpark Pass for games that I do not attend?",
    answer:
      "No. The Yankees Ballpark Pass cannot be resold, forwarded or transferred. Any attempted resale will subject the Yankees Ballpark Pass to immediate cancellation without refund and restrict the purchaser from any future Yankees Ballpark Pass purchases.",
  },
  {
    question: "Can I exchange my Yankees Ballpark Pass for another game date?",
    answer: "No. The Yankees Ballpark Pass cannot be exchanged.",
  },
  {
    question: "Can I share my Yankees Ballpark Pass with someone else?",
    answer:
      "No. The Yankees Ballpark Pass cannot be shared. The Yankees Ballpark Pass is non-transferable and must be managed on the iPhone/Android phone of the purchaser. All persons entering Yankee Stadium with the Yankees Ballpark Pass must enter with the original purchaser.",
  },
  {
    question: "How do my Guests enter the Stadium?",
    answer:
      "The Yankees Ballpark Pass is non-transferable and must be managed on the iPhone/Android phone of the purchaser. All persons entering Yankee Stadium with the Yankees Ballpark Pass must enter with the original purchaser.",
  },
  {
    question: "Can I upgrade my Yankees Ballpark Pass to a specific seat?",
    answer:
      "While the Yankees Ballpark Pass only guarantees general admission access to Yankee Stadium in a non-assigned Standing-Room-Only location, for some games, you may have an opportunity to upgrade to a designated non-premium seat, subject to availability, via your My Yankees Account on the day of the game. Fans who wish to sit together must purchase all Yankees Ballpark Passes in one (1) transaction. THERE IS NO GUARANTEE THAT ANY NON-PREMIUM SEATS WILL BE MADE AVAILABLE. NON-PREMIUM SEAT UPGRADES ARE IN THE SOLE AND ABSOLUTE DISCRETION OF THE YANKEES.",
  },
  {
    question: "What happens if a game is rained out?",
    answer:
      "In the event a regulation baseball game (as defined in Official Baseball Rule 7.01) is not played due to weather, an act of God or any other reason, the Rain Check Policy will apply.",
  },
  {
    question:
      "Can I renew the Yankees Ballpark Pass for future months/seasons?",
    answer:
      "No. The Yankees Ballpark Pass is only valid for Yankees’ home games originally scheduled to be played in the month that it is valid.",
  },
];

export const TERMS_AND_CONDITION = () => {
  const theme = useSSTheme();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExt();
  return (
    <>
      In connection with your purchase, your personal information will be shared
      with the Yankees for fulfillment purposes and you will receive
      ticket-related commercial emails from yankees.com. You may unsubscribe at
      any time. If you purchase tickets, you may receive customer service
      messages via e-mail from the New York Yankees, including optional surveys
      regarding your ballpark experience. Your personal information will be
      processed in accordance with the Yankees{" "}
      <Link
        href={"https://www.mlb.com/official-information/privacy-policy"}
        target="_blank"
      >
        Privacy Policy
      </Link>
      . By purchasing tickets, you accept all{" "}
      <Link
        href={"https://www.mlb.com/yankees/tickets/terms-and-conditions"}
        target="_blank"
      >
        Terms and Conditions
      </Link>{" "}
      as well as policies applicable to use. All sales are final. There are no
      refunds or exchanges.
    </>
  );
};
